'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { reportHandledError } from '@/lib/frontend-error-reporter';

/**
 * 라우트의 병원 식별자를 해석할 수 없을 때 쓰는 경계.
 *
 * **요청은 하지 않되, 왜 막았는지는 반드시 남긴다.**
 * 종전에는 이런 주소가 `GET /user/apis/unrestricted/store/NaN` 을 실제로 발사했고,
 * 그 4xx 를 리포터의 fetch 모니터가 자동으로 올려 줘서 우리가 이 버그를 알 수 있었다.
 * 요청만 막고 끝내면 그 **유일한 신호가 함께 사라진다** — 그래서 여기서 명시적으로 올린다.
 *
 * ⚠️ 서버 컴포넌트에서 `notFound()` 로 바로 끝내면 안 되는 이유가 이것이다.
 * 리포터는 `window` 가 없으면 즉시 return 하므로 서버에서는 한 줄도 올라가지 않는다.
 */
export default function InvalidAppStoreIdBoundary({ rawParam, routePattern }: { rawParam?: string | string[] | null; routePattern: string }) {
  const router = useRouter();
  const reported = useRef(false);

  useEffect(() => {
    // StrictMode 이중 마운트로 같은 사건이 두 번 올라가지 않게 한다.
    if (reported.current) return;
    reported.current = true;

    const received = Array.isArray(rawParam) ? rawParam.join('/') : String(rawParam);

    // ⚠️ 메시지에 `received` 를 넣으면 안 된다 — 게이트웨이 fingerprint 에 메시지가 들어가므로
    //    쓰레기 주소 하나마다 큐에 새 케이스가 생긴다(카디널리티 무한).
    //    메시지는 라우트 단위로 고정하고, 실제 값은 fingerprint 에 안 들어가는 request.path 로 보낸다.
    //    (현재 주소 전체는 리포터가 `page.path` 로 이미 싣는다)
    reportHandledError(`invalid appStoreId route param on ${routePattern}`, { method: 'GET', path: received.slice(0, 64) });

    router.replace('/not-found');
  }, [rawParam, routePattern, router]);

  return null;
}
